import React, { useState } from 'react';
import { api } from '../../lib/api';
import { StarField } from './StarField';
import { Link } from 'react-router-dom';
import '../../styles/home.css';

interface AstroPreview {
  sun: string;
  moon: string | null;
}

// Dicionário de frases por signo solar (tom da Stela — específico de cada signo)
const SOL_PHRASES: Record<string, string> = {
  Áries: 'imposição de ritmo: ninguém define seu caminho — você traça, e depois avisa.',
  Touro: 'pesa antes de mover: quando resolve, até o chão cede ao seu tempo.',
  Gêmeos: 'precisa de dois fios puxando ao mesmo tempo — monotonia te apaga.',
  Câncer: 'protege antes de se mostrar: o cuidado é a sua primeira língua.',
  Leão: 'não divide palco com quem apaga o seu brilho. Sabia disso há tempo.',
  Virgem: 'enxerga o fio solto que ninguém viu — e não dorme até puxar.',
  Libra: 'pesa os dois lados antes de responder; a balança nunca dorme.',
  Escorpião: 'vai fundo ou não vai: meia-presença não te sustenta.',
  Sagitário: 'aponta horizonte e sai andando — o mapa serve a estrada.',
  Capricórnio: 'constrói com o relógio na mão: seu tempo é patrimônio.',
  Aquário: 'vê o padrão que ninguém agrupou — e fala antes de pedir permissão.',
  Peixes: 'sente a sala inteira antes da primeira porta. Filtro não falta; limite, sim.',
};

export const Hero: React.FC = () => {
  const [nome, setNome] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<AstroPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nascimento) {
      setError('Informe a data de nascimento');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { sun, moon } = await api.get<AstroPreview>(
        `/astro/preview?date=${encodeURIComponent(nascimento)}`,
      );
      setPreview({ sun, moon });
      try {
        sessionStorage.setItem('stela.lead', JSON.stringify({ nome: nome.trim(), email: '', whatsapp: '' }));
        sessionStorage.setItem('stela.preview', JSON.stringify({ sun, moon, nome: nome.trim() }));
      } catch {
        // storage bloqueado — funciona sem memória
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não deu para ler seus astros agora. Tente de novo.');
    } finally {
      setLoading(false);
    }
  };

  const phrase = preview
    ? SOL_PHRASES[preview.sun] ?? 'imprime o mundo com o seu jeito de estar — e é por ali que a leitura começa.'
    : '';

  return (
    <section className="relative overflow-hidden">
      <StarField className="absolute inset-0 h-full w-full pointer-events-none" />

      <div className="relative hero">
        <div>
          <span className="badge">✦ Sua sensitiva astróloga, 24h · grátis</span>
          <h1 className="display">
            Descubra o seu <em className="font-semibold not-italic text-stela-lilac">caminho cósmico</em>
          </h1>
          <p className="lead">
            O Stela Guia calcula o seu mapa natal e conversa com você para interpretar amor,
            carreira e o que os astros reservam — na sua jornada, do primeiro mapa à leitura diária.
          </p>
          <a href="#captura" className="btn">Começar meu mapa grátis</a>
        </div>

        <form onSubmit={submit} id="captura" className="capture">
          <span className="free">Prévia gratuita</span>
          <h3 className="display">Leia seu horóscopo personalizado</h3>

          <label htmlFor="hero-nome">Seu nome</label>
          <input
            id="hero-nome"
            autoComplete="name"
            placeholder="Como se chama?"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />

          <label htmlFor="hero-nasc">Data de nascimento</label>
          <input
            id="hero-nasc"
            type="date"
            value={nascimento}
            onChange={(e) => setNascimento(e.target.value)}
            aria-describedby={error ? 'hero-error' : undefined}
          />

          <button className="btn" type="submit" disabled={loading}>
            {loading ? 'Lendo seus astros…' : 'Ver minha prévia grátis'}
          </button>

          {error && (
            <p id="hero-error" role="alert" aria-live="assertive" style={{ color: '#E08C8C', fontSize: 12.5, marginTop: 12 }}>
              {error}
            </p>
          )}

          {preview && (
            <div className="preview-panel" role="region" aria-live="polite">
              <div className="sign display">
                ☀ Sol em {preview.sun}
                {preview.moon ? ` · ☾ Lua em ${preview.moon}` : ''}
              </div>
              <p>
                {nome || 'Você'}, seu Sol em {preview.sun} {phrase}
                {preview.moon
                  ? ' A Lua mostra como você sente quando ninguém está vendo.'
                  : ' Com a hora do nascimento, a Lua completa a leitura dentro da conversa.'}{' '}
                <Link to="/chat?via=preview" style={{ color: 'var(--gold)', fontWeight: 600 }}>
                  Continuar na conversa com Stela →
                </Link>
              </p>
            </div>
          )}

          <p className="tiny">Sem spam — a data de nascimento personaliza suas leituras.</p>
        </form>
      </div>
    </section>
  );
};